<template>
  <div
    class="mobile-mega-menu"
    :style="{
      background:
        theme.appBarBg ||
        theme.background,

      color:
        theme.appBarColor ||
        theme.color
    }"
  >

    <!-- ===================================================== -->
    <!-- LOADING -->
    <!-- ===================================================== -->
    <div
      v-if="loading"
      class="mobile-menu-state"
    >
      <v-progress-circular
        indeterminate
        size="38"
        width="3"
        color="primary"
      />
    </div>


    <!-- ===================================================== -->
    <!-- ERROR -->
    <!-- ===================================================== -->
    <v-alert
      v-else-if="loadError"
      type="error"
      variant="tonal"
      density="comfortable"
      class="ma-3"
    >
      {{ loadError }}
    </v-alert>


    <!-- ===================================================== -->
    <!-- MOBILE MENU -->
    <!-- ===================================================== -->
    <v-list
      v-else
      v-model:opened="openedGroups"
      class="mobile-menu-list"
      density="comfortable"
      nav
      :style="{
        background:
          theme.appBarBg ||
          theme.background,

        color:
          theme.appBarColor ||
          theme.color
      }"
    >

      <template
        v-for="main in sortedMainMenus"
        :key="main.id"
      >

        <!-- ================================================= -->
        <!-- MAIN MENU WITH DROPDOWN -->
        <!-- ================================================= -->
        <v-list-group
          v-if="toBoolean(main.hasDropDown)"
          :value="`main-${main.id}`"
          class="mobile-main-group"
        >
          <template #activator="{ props }">
            <v-list-item
              v-bind="props"
              class="mobile-main-item"
            >
              <template #prepend>
                <v-icon
                  v-if="
                    toBoolean(main.showIcon) &&
                    main.mdiIcon
                  "
                  :icon="main.mdiIcon"
                  size="22"
                />
              </template>

              <v-list-item-title
                class="mobile-main-title"
              >
                {{
                  getLocalizedText(
                    main,
                    "desc",
                    "descFR"
                  )
                }}
              </v-list-item-title>
            </v-list-item>
          </template>


          <!-- =============================================== -->
          <!-- COLUMNS -->
          <!-- =============================================== -->
          <template
            v-for="col in getColumnsForMain(main.id)"
            :key="col.colId"
          >

            <!-- ============================================= -->
            <!-- COLUMN WITH ITEMS -->
            <!-- ============================================= -->
            <v-list-group
              v-if="
                toBoolean(col.hasDropDown) &&
                getItemsForColumn(col.colId).length > 0
              "
              :value="`column-${col.colId}`"
              class="mobile-column-group"
            >
              <template #activator="{ props }">
                <v-list-item
                  v-bind="props"
                  class="mobile-column-item"
                >
                  <template #prepend>
                    <v-icon
                      v-if="col.mdiIcon"
                      :icon="col.mdiIcon"
                      size="21"
                      class="column-icon"
                    />
                  </template>

                  <v-list-item-title>
                    {{
                      getLocalizedText(
                        col,
                        "desc",
                        "descFR"
                      )
                    }}
                  </v-list-item-title>
                </v-list-item>
              </template>


              <!-- =========================================== -->
              <!-- CHILD MENU ITEMS -->
              <!-- =========================================== -->
              <template
                v-for="item in getItemsForColumn(col.colId)"
                :key="item.id"
              >

                <!-- CLICKABLE CHILD ITEM -->
                <component
                  :is="
                    getMenuHref(item)
                      ? 'a'
                      : 'router-link'
                  "
                  v-if="isMenuItemClickable(item)"
                  :to="getMenuRoute(item)"
                  :href="getMenuHref(item)"
                  :target="
                    getMenuHref(item)
                      ? '_blank'
                      : undefined
                  "
                  :rel="
                    getMenuHref(item)
                      ? 'noopener noreferrer'
                      : undefined
                  "
                  class="mobile-menu-link"
                  @click="handleNavigation"
                >
                  <v-list-item
                    class="mobile-child-item"
                  >
                    <template #prepend>
                      <v-icon
                        v-if="item.mdiIcon"
                        :icon="item.mdiIcon"
                        size="21"
                      />
                    </template>

                    <v-list-item-title
                      class="mobile-child-title"
                    >
                      {{
                        getLocalizedText(
                          item,
                          "mainDesc",
                          "mainDescFR"
                        )
                      }}
                    </v-list-item-title>

                    <v-list-item-subtitle
                      v-if="
                        getLocalizedText(
                          item,
                          'subDesc',
                          'subDescFR'
                        )
                      "
                      class="mobile-child-subtitle"
                    >
                      {{
                        getLocalizedText(
                          item,
                          "subDesc",
                          "subDescFR"
                        )
                      }}
                    </v-list-item-subtitle>

                    <template #append>
                      <v-icon
                        size="18"
                        icon="mdi-chevron-right"
                      />
                    </template>
                  </v-list-item>
                </component>


                <!-- NON-CLICKABLE CHILD -->
                <v-list-item
                  v-else
                  disabled
                  class="mobile-child-item"
                >
                  <template #prepend>
                    <v-icon
                      v-if="item.mdiIcon"
                      :icon="item.mdiIcon"
                      size="21"
                    />
                  </template>

                  <v-list-item-title>
                    {{
                      getLocalizedText(
                        item,
                        "mainDesc",
                        "mainDescFR"
                      )
                    }}
                  </v-list-item-title>

                  <v-list-item-subtitle>
                    {{
                      getLocalizedText(
                        item,
                        "subDesc",
                        "subDescFR"
                      )
                    }}
                  </v-list-item-subtitle>
                </v-list-item>

              </template>
            </v-list-group>


            <!-- ============================================= -->
            <!-- COLUMN WITHOUT DROPDOWN -->
            <!-- Same behavior as desktop MegaMenuDropDown -->
            <!-- ============================================= -->
            <v-list-item
              v-else
              class="mobile-column-item"
            >
              <template #prepend>
                <v-icon
                  v-if="col.mdiIcon"
                  :icon="col.mdiIcon"
                  size="21"
                />
              </template>

              <v-list-item-title>
                {{
                  getLocalizedText(
                    col,
                    "desc",
                    "descFR"
                  )
                }}
              </v-list-item-title>
            </v-list-item>

          </template>


          <!-- =============================================== -->
          <!-- EMPTY MAIN MENU -->
          <!-- =============================================== -->
          <v-list-item
            v-if="
              getColumnsForMain(main.id).length === 0
            "
            disabled
            class="mobile-empty-item"
          >
            <v-list-item-title>
              {{
                currentLanguage === "fr"
                  ? "Aucun élément disponible"
                  : "No items available"
              }}
            </v-list-item-title>
          </v-list-item>

        </v-list-group>


        <!-- ================================================= -->
        <!-- DIRECT MAIN MENU LINK -->
        <!-- ================================================= -->
        <component
          :is="
            getMenuHref(main)
              ? 'a'
              : 'router-link'
          "
          v-else-if="
            isMainMenuNavigatable(main)
          "
          :to="getMenuRoute(main)"
          :href="getMenuHref(main)"
          :target="
            toBoolean(main.openInNewTab)
              ? '_blank'
              : undefined
          "
          :rel="
            toBoolean(main.openInNewTab)
              ? 'noopener noreferrer'
              : undefined
          "
          class="mobile-menu-link"
          @click="handleNavigation"
        >
          <v-list-item
            class="mobile-main-item"
          >
            <template #prepend>
              <v-icon
                v-if="
                  toBoolean(main.showIcon) &&
                  main.mdiIcon
                "
                :icon="main.mdiIcon"
                size="22"
              />
            </template>

            <v-list-item-title
              class="mobile-main-title"
            >
              {{
                getLocalizedText(
                  main,
                  "desc",
                  "descFR"
                )
              }}
            </v-list-item-title>

            <template #append>
              <v-icon
                size="18"
                icon="mdi-chevron-right"
              />
            </template>
          </v-list-item>
        </component>


        <!-- ================================================= -->
        <!-- MAIN MENU WITH NO DESTINATION -->
        <!-- ================================================= -->
        <v-list-item
          v-else
          disabled
          class="mobile-main-item"
        >
          <template #prepend>
            <v-icon
              v-if="
                toBoolean(main.showIcon) &&
                main.mdiIcon
              "
              :icon="main.mdiIcon"
              size="22"
            />
          </template>

          <v-list-item-title
            class="mobile-main-title"
          >
            {{
              getLocalizedText(
                main,
                "desc",
                "descFR"
              )
            }}
          </v-list-item-title>
        </v-list-item>

      </template>

    </v-list>


    <!-- ===================================================== -->
    <!-- ERROR SNACKBAR -->
    <!-- ===================================================== -->
    <v-snackbar
      :model-value="Boolean(loadError)"
      color="error"
      timeout="8000"
    >
      {{ loadError }}
    </v-snackbar>

  </div>
</template>


<script setup>
import {
  computed,
  onMounted,
  ref,
  watch
} from "vue"

import {
  useI18n
} from "vue-i18n"

import publicApi
  from "@/auth/publicApi"

import {
  useLangStore
} from "@/store/lang"

import {
  useThemeStore
} from "@/store/auth"


/* ============================================================
   EMITS
============================================================ */

const emit = defineEmits([
  "navigate"
])


/* ============================================================
   STORES
============================================================ */

const lang =
  useLangStore()

const themeStore =
  useThemeStore()

const {
  locale
} = useI18n()


/* ============================================================
   DATA
============================================================ */

const megaMainMenu =
  ref([])

const megaColumn =
  ref([])

const megaColumnItem =
  ref([])

const megaColumnImage =
  ref([])

const dynamicPages =
  ref([])


/* ============================================================
   STATE
============================================================ */

const loading =
  ref(false)

const loadError =
  ref("")

const openedGroups =
  ref([])


/* ============================================================
   LANGUAGE
============================================================ */

const currentLanguage =
  computed(
    () =>
      lang.currentLanguage ||
      "en"
  )


watch(
  currentLanguage,
  newLanguage => {
    locale.value =
      newLanguage ||
      "en"
  },
  {
    immediate: true
  }
)


/* ============================================================
   THEME
============================================================ */

const theme =
  computed(
    () =>
      themeStore.themeStyles ||
      {
        appBarBg:
          "#ffffff",

        appBarColor:
          "#000000"
      }
  )


/* ============================================================
   NORMALIZE ARRAY
============================================================ */

function normalizeArray(
  data
) {
  if (
    Array.isArray(data)
  ) {
    return data
  }

  if (
    Array.isArray(
      data?.data
    )
  ) {
    return data.data
  }

  if (
    Array.isArray(
      data?.items
    )
  ) {
    return data.items
  }

  if (
    Array.isArray(
      data?.results
    )
  ) {
    return data.results
  }

  if (
    Array.isArray(
      data?.value
    )
  ) {
    return data.value
  }

  return []
}


/* ============================================================
   BOOLEAN NORMALIZATION
============================================================ */

function toBoolean(
  value
) {
  if (
    typeof value ===
    "boolean"
  ) {
    return value
  }

  if (
    typeof value ===
    "number"
  ) {
    return value === 1
  }

  if (
    typeof value ===
    "string"
  ) {
    return [
      "true",
      "1",
      "yes",
      "on"
    ].includes(
      value
        .trim()
        .toLowerCase()
    )
  }

  return false
}


/* ============================================================
   ID COMPARISON
============================================================ */

function sameId(
  firstId,
  secondId
) {
  if (
    firstId == null ||
    secondId == null
  ) {
    return false
  }

  return (
    String(firstId)
      .trim() ===
    String(secondId)
      .trim()
  )
}


/* ============================================================
   MAIN MENU SORTING
============================================================ */

const sortedMainMenus =
  computed(
    () =>
      [...megaMainMenu.value]
        .filter(
          main =>
            main?.isVisible !== false
        )
        .sort(
          (
            first,
            second
          ) =>
            Number(
              first?.colOrder ||
              0
            ) -
            Number(
              second?.colOrder ||
              0
            )
        )
  )


/* ============================================================
   COLUMNS
============================================================ */

function getColumnsForMain(
  mainMenuId
) {
  return megaColumn.value
    .filter(
      column =>
        sameId(
          column.mainMenuId,
          mainMenuId
        )
    )
    .sort(
      (
        first,
        second
      ) =>
        Number(
          first?.colOrder ||
          0
        ) -
        Number(
          second?.colOrder ||
          0
        )
    )
}


/* ============================================================
   ITEMS
============================================================ */

function getItemsForColumn(
  columnId
) {
  return megaColumnItem.value
    .filter(
      item =>
        sameId(
          item.colId,
          columnId
        )
    )
    .sort(
      (
        first,
        second
      ) =>
        Number(
          first?.menuorder ||
          0
        ) -
        Number(
          second?.menuorder ||
          0
        )
    )
}


/* ============================================================
   LOCALIZED TEXT
============================================================ */

function getLocalizedText(
  record,
  englishProperty,
  frenchProperty
) {
  if (!record) {
    return ""
  }

  if (
    currentLanguage.value ===
    "fr"
  ) {
    return (
      record[frenchProperty] ||
      record[englishProperty] ||
      ""
    )
  }

  return (
    record[englishProperty] ||
    record[frenchProperty] ||
    ""
  )
}


/* ============================================================
   CLEAN VALUE
============================================================ */

function cleanValue(
  value
) {
  const normalized =
    String(
      value ??
      ""
    )
      .trim()

  if (
    !normalized ||
    normalized === "#" ||
    normalized
      .toLowerCase() ===
      "null" ||
    normalized
      .toLowerCase() ===
      "undefined"
  ) {
    return ""
  }

  return normalized
}


/* ============================================================
   EXTERNAL URL
============================================================ */

function isExternalUrl(
  value
) {
  return /^(https?:)?\/\//i
    .test(
      cleanValue(
        value
      )
    )
}


/* ============================================================
   DYNAMIC PAGE LOOKUP
============================================================ */

function getDynamicPage(
  item
) {
  if (
    !item?.dynamicPageId
  ) {
    return undefined
  }

  return dynamicPages.value
    .find(
      page =>
        sameId(
          page.id,
          item.dynamicPageId
        )
    )
}


/* ============================================================
   DYNAMIC PAGE SLUG
============================================================ */

function getDynamicPageSlug(
  item
) {
  const page =
    getDynamicPage(
      item
    )

  if (!page) {
    return undefined
  }

  if (
    currentLanguage.value ===
    "fr"
  ) {
    return cleanValue(
      page.slugFr ??
      page.slugEn ??
      ""
    )
  }

  return cleanValue(
    page.slugEn ??
    page.slugFr ??
    ""
  )
}


/* ============================================================
   STATIC ROUTE VALUE
============================================================ */

function getStaticRouteValue(
  item
) {
  if (
    currentLanguage.value ===
    "fr"
  ) {
    return cleanValue(
      item?.menuPathFr ||
      item?.menuPathEn ||
      item?.linkPathFr ||
      item?.linkPath
    )
  }

  return cleanValue(
    item?.menuPathEn ||
    item?.menuPathFr ||
    item?.linkPath ||
    item?.linkPathFr
  )
}


/* ============================================================
   EXTERNAL LINK VALUE
============================================================ */

function getExternalLink(
  item
) {
  if (
    currentLanguage.value ===
    "fr"
  ) {
    return cleanValue(
      item?.linkPathFr ||
      item?.linkPath
    )
  }

  return cleanValue(
    item?.linkPath ||
    item?.linkPathFr
  )
}


/* ============================================================
   ROUTER DESTINATION
============================================================ */

function getMenuRoute(
  item
) {
  if (!item) {
    return undefined
  }


  /* ----------------------------------------------------------
     DYNAMIC PAGE
  ---------------------------------------------------------- */

  if (
    item.dynamicPageId
  ) {
    const page =
      getDynamicPage(
        item
      )

    if (!page) {
      return undefined
    }

    if (
      page.isPublished ===
      false
    ) {
      return undefined
    }

    const slug =
      getDynamicPageSlug(
        item
      )

    if (
      slug ===
      undefined
    ) {
      return undefined
    }

    const pagePath =
      slug
        ? slug
            .split("/")
            .map(
              segment =>
                segment.trim()
            )
            .filter(
              Boolean
            )
        : []

    return {
      name:
        "DynamicPublicPage",

      params: {
        lang:
          currentLanguage.value,

        pagePath
      }
    }
  }


  /* ----------------------------------------------------------
     STATIC ROUTE
  ---------------------------------------------------------- */

  const routeValue =
    getStaticRouteValue(
      item
    )

  if (
    !routeValue ||
    isExternalUrl(
      routeValue
    )
  ) {
    return undefined
  }

  if (
    routeValue.startsWith(
      "/"
    )
  ) {
    return {
      path:
        routeValue
    }
  }

  return {
    name:
      routeValue
  }
}


/* ============================================================
   EXTERNAL HREF
============================================================ */

function getMenuHref(
  item
) {
  if (
    !item ||
    item.dynamicPageId
  ) {
    return undefined
  }

  const link =
    getExternalLink(
      item
    )

  return isExternalUrl(
    link
  )
    ? link
    : undefined
}


/* ============================================================
   CHILD ITEM CLICKABILITY
============================================================ */

function isMenuItemClickable(
  item
) {
  if (!item) {
    return false
  }

  if (
    item.dynamicPageId
  ) {
    return Boolean(
      getMenuRoute(
        item
      )
    )
  }

  if (
    item.isLink !==
      undefined &&
    item.isLink !==
      null &&
    !toBoolean(
      item.isLink
    )
  ) {
    return false
  }

  return Boolean(
    getMenuRoute(
      item
    ) ||
    getMenuHref(
      item
    )
  )
}


/* ============================================================
   MAIN MENU DIRECT LINK RULE
============================================================ */

function isMainMenuNavigatable(
  main
) {
  if (!main) {
    return false
  }

  if (
    toBoolean(
      main.hasDropDown
    )
  ) {
    return false
  }

  return Boolean(
    getMenuRoute(
      main
    ) ||
    getMenuHref(
      main
    )
  )
}


/* ============================================================
   NAVIGATION EVENT
============================================================ */

function handleNavigation() {
  emit(
    "navigate"
  )
}


/* ============================================================
   LOAD MENU
============================================================ */

async function loadMegaMenu() {
  loading.value =
    true

  loadError.value =
    ""

  try {
    const [
      mainMenuResponse,
      columnResponse,
      itemResponse,
      imageResponse,
      dynamicPageResponse
    ] =
      await Promise.all([
        publicApi.get(
          "/MainMenu"
        ),

        publicApi.get(
          "/MegamenuColumn"
        ),

        publicApi.get(
          "/MegamenuItems"
        ),

        publicApi.get(
          "/MegamenuImages"
        ),

        publicApi.get(
          "/dynamic-pages"
        )
      ])


    megaMainMenu.value =
      normalizeArray(
        mainMenuResponse.data
      )

    megaColumn.value =
      normalizeArray(
        columnResponse.data
      )

    megaColumnItem.value =
      normalizeArray(
        itemResponse.data
      )

    megaColumnImage.value =
      normalizeArray(
        imageResponse.data
      )

    dynamicPages.value =
      normalizeArray(
        dynamicPageResponse.data
      )
  }
  catch (error) {
    console.error(
      "Unable to load mobile mega menu:",
      error
    )

    loadError.value =
      error?.response
        ?.data
        ?.message ||
      error?.response
        ?.data
        ?.detail ||
      error?.message ||
      "Unable to load the menu."
  }
  finally {
    loading.value =
      false
  }
}


/* ============================================================
   INITIALIZE
============================================================ */

onMounted(
  loadMegaMenu
)
</script>


<style scoped>
.mobile-mega-menu {
  width: 100%;
  min-height: 100%;
}


.mobile-menu-state {
  display: flex;
  min-height: 180px;

  align-items: center;
  justify-content: center;
}


.mobile-menu-list {
  padding: 8px 0 22px;
}


/* ============================================================
   MAIN MENU
============================================================ */

.mobile-main-item {
  min-height: 58px;

  padding-right: 18px;
  padding-left: 18px;

  border-bottom:
    1px solid rgba(127, 127, 127, 0.12);
}


.mobile-main-title {
  font-size: 15px;
  font-weight: 750;
}


/* ============================================================
   COLUMN
============================================================ */

.mobile-column-item {
  min-height: 54px;

  margin:
    2px 10px;

  padding-left:
    18px !important;

  border-radius:
    8px;
}


.mobile-column-group {
  margin-left:
    6px;
}


.column-icon {
  color:
    #187052;
}


/* ============================================================
   CHILD ITEMS
============================================================ */

.mobile-child-item {
  min-height: 62px;

  margin:
    5px 14px
    5px 28px;

  border:
    1px solid
    rgba(12, 47, 82, 0.08);

  border-radius:
    10px;
}


.mobile-child-item:hover {
  background:
    rgba(
      37,
      135,
      101,
      0.06
    );
}


.mobile-child-title {
  font-size:
    14px;

  font-weight:
    700;

  white-space:
    normal;
}


.mobile-child-subtitle {
  margin-top:
    3px;

  white-space:
    normal !important;

  overflow-wrap:
    anywhere;

  line-height:
    1.4;
}


/* ============================================================
   LINKS
============================================================ */

.mobile-menu-link {
  display:
    block;

  color:
    inherit;

  text-decoration:
    none;
}


.mobile-menu-link:hover {
  color:
    inherit;

  text-decoration:
    none;
}


/* ============================================================
   EMPTY
============================================================ */

.mobile-empty-item {
  margin-left:
    18px;

  opacity:
    0.62;
}


/* ============================================================
   VUETIFY NESTING
============================================================ */

.mobile-main-group
  :deep(.v-list-group__items) {
  --indent-padding:
    12px;
}


.mobile-column-group
  :deep(.v-list-group__items) {
  --indent-padding:
    10px;
}


:deep(
  .v-list-group__items
  .v-list-item
) {
  padding-inline-start:
    16px !important;
}
</style>